import React from 'react';
import { Badge, Button, Progress } from 'antd';
import { AntdTable } from '../../components/AntdTable';
import { ColumnsType } from 'antd/es/table';
import { formatIdOrder } from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectStockOrderHistoryData,
    selectStockOrderHistoryLoading,
} from '../../modules/user/stock-order-history/selector';
import moment from 'moment';
import { OpenOrderData } from '../../modules/user/stock-order/types';
import { fetchCancelOrderRequest } from '../../modules/user/stock-order/actions';
import { AntdModal } from '../../components/AntdModal';
import { selectCancelOrderLoading } from '../../modules/user/stock-order/selector';
import { RootState } from '../../modules';
import { fetchOrderHistoryRequest, OrderHistoryStatus } from '../../modules/user/stock-order-history';

interface SearchProps {
    searchVal?: string;
}

export interface DataOrderType {
    key: string;
    id: string;
    ticker: string;
    type: 'market' | 'limit';
    side: 'sell' | 'buy';
    totalAmount: number;
    price: number;
    status: OrderHistoryStatus;
    filledAmount: number;
    createdAt: string;
}

const getStatusBadge = (status: OrderHistoryStatus) => {
    let color: 'success' | 'processing' | 'error' | 'default' = 'default';
    let text = '';

    switch (status) {
        case 'pending':
            color = 'processing';
            text = 'Pending';
            break;
        case 'partially_filled':
            color = 'processing';
            text = 'Partially Filled';
            break;
        case 'completed':
            color = 'success';
            text = 'Completed';
            break;
        case 'cancelled':
            color = 'error';
            text = 'Cancelled';
            break;
        default:
            break;
    }

    return <Badge style={{ color: 'white' }} status={color} text={text} />;
};

const statusSortOrder: any = {
    cancelled: 0,
    pending: 1,
    partially_filled: 2,
    completed: 3,
};

export const sortOrder: { [key: string]: number } = {
    buy: 0,
    sell: 1,
    market: 0,
    limit: 1,
};

export const TableOrderContainer: React.FC<SearchProps> = (props: any) => {
    const { searchVal } = props;
    const dispatch = useDispatch();
    const data = useSelector(selectStockOrderHistoryData) as unknown as DataOrderType[];
    const [filteredData, setFilteredData] = React.useState<DataOrderType[]>(data);

    const isDataLoading = useSelector(selectStockOrderHistoryLoading);

    const [openCancelModal, setOpenCancelModal] = React.useState(false);
    const cancelLoading = useSelector((state: RootState) => selectCancelOrderLoading(state));
    const [cancel, setCancel] = React.useState<OpenOrderData>();

    const showModal = (cancel: OpenOrderData) => {
        setOpenCancelModal(true);
        setCancel(cancel);
    };
    const cancelModal = () => {
        setOpenCancelModal(false);
        setCancel(undefined);
    };
    const handleCancelOrder = () => {
        if (cancel) {
            dispatch(fetchCancelOrderRequest({ id: cancel.id, ticker: cancel.ticker }));
        }
    };
    React.useEffect(() => {
        if (!cancelLoading) {
            setOpenCancelModal(false);
            setCancel(undefined);
        }
    }, [cancelLoading]);
    const canCancelled = (status: OrderHistoryStatus) => {
        return status === 'partially_filled' || status === 'pending';
    };

    React.useEffect(() => {
        if (!searchVal) {
            setFilteredData(data);
        } else {
            setFilteredData(data.filter((item: any) => formatIdOrder(item.id, 'o').toString().includes(searchVal)));
        }
    }, [searchVal, data]);
    const columns: ColumnsType<any> = [
        {
            title: 'ID Order',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a.id - b.id,
            render: (id) => formatIdOrder(id, 'o'),
        },
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            key: 'ticker',
            sorter: (a, b) => a.ticker.localeCompare(b.ticker),
            render: (text) => <a>{text}</a>,
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => (moment(a.createdAt).isBefore(b.createdAt) ? -1 : 1),
            render: (val) => moment(val).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            sorter: (a, b) => sortOrder[a.type] - sortOrder[b.type],
            key: 'type',
        },
        {
            title: 'Side',
            dataIndex: 'side',
            sorter: (a, b) => sortOrder[a.side] - sortOrder[b.side],
            key: 'side',
            render: (side) => <b style={{ color: side === 'buy' ? 'green' : 'red' }}>{side.toUpperCase()}</b>,
        },
        {
            title: 'Amount',
            dataIndex: 'totalAmount',
            sorter: (a, b) => a.totalAmount - b.totalAmount,
            key: 'totalAmount',
            render: (value: any) => {
                return Number(value).toLocaleString();
            },
        },
        {
            title: 'Price',
            dataIndex: 'price',
            sorter: (a, b) => a.price - b.price,
            key: 'price',
            render: (value: any) => {
                return Number(value).toLocaleString();
            },
        },
        {
            title: 'Total(VND)',
            dataIndex: 'total',
            key: 'total',
            render: (text, record) => {
                return (
                    <span style={{ color: 'var(--primary-text-color-main)' }}>
                        {Number(record.price * record.totalAmount).toLocaleString()}
                    </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: any, b: any) => statusSortOrder[a.status] - statusSortOrder[b.status],
            render: (status) => getStatusBadge(status),
        },
        {
            title: 'Item Received',
            dataIndex: 'filledAmount',
            key: 'filledAmount',
            sorter: (a, b) => a.filledAmount - b.filledAmount,
            render: (filledAmount, record) => {
                return (
                    <Progress
                        percent={Math.round(100 * (filledAmount / record.totalAmount) * 100) / 100}
                        strokeColor="#3fcb58"
                        trailColor="#a5b6b6"
                    />
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (record) =>
                canCancelled(record.status) && (
                    <Button key={record.id} onClick={() => showModal(record)} danger ghost>
                        Cancel
                    </Button>
                ),
        },
    ];

    return (
        <>
            <AntdTable
                className="custom-table"
                columns={columns}
                dataSource={filteredData}
                loading={isDataLoading}
                pagination={{ pageSize: 15 }}
            />
            <AntdModal
                title="Are you sure to cancel this order?"
                open={openCancelModal}
                onCancel={cancelModal}
                onOk={handleCancelOrder}
                loading={cancelLoading}
            />
        </>
    );
};
